import React, { useState, useEffect } from "react";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { BellRing, Volume2 } from "lucide-react";
import { toast } from "sonner";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function playChimeSound() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    const playTone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0.25, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    };

    playTone(880, 0, 0.15); // A5
    playTone(1318.5, 0.12, 0.35); // E6
  } catch (err) {
    // browser audio policy
  }
}

export default function PushNotificationPrompt() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      setSupported(true);
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => {
          if (sub) {
            setSubscribed(true);
          }
        })
        .catch(() => {});
    }
  }, []);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.warning("Notification permission was denied. Please allow it in the browser address bar lock icon.");
        setLoading(false);
        return;
      }

      const res = await apiClient.get("/notifications/vapid-public-key");
      const publicKey = res.data.public_key;

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      await apiClient.post("/notifications/subscribe", sub.toJSON());

      setSubscribed(true);
      playChimeSound();

      // Show immediate notification
      reg.showNotification("🔔 AgencyOS Daily Alerts Enabled", {
        body: "You will now receive sound and push reminders for your daily schedule!",
        icon: "/favicon.ico",
        badge: "/favicon.ico",
      });

      toast.success("9:00 AM Daily Alerts & Sound Enabled on this device!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not enable push notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleTestAlert = async () => {
    try {
      // 1. Play audio chime
      playChimeSound();

      // 2. Display local OS notification banner immediately
      if ("Notification" in window && Notification.permission === "granted") {
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.ready.then((reg) => {
            reg.showNotification("🔔 AgencyOS — Test Alert", {
              body: "Sound and banner notifications are active for your account!",
              icon: "/favicon.ico",
              badge: "/favicon.ico",
              vibrate: [200, 100, 200],
            });
          });
        } else {
          new Notification("🔔 AgencyOS — Test Alert", {
            body: "Sound and banner notifications are active for your account!",
            icon: "/favicon.ico",
          });
        }
      }

      // 3. Dispatch server webpush
      await apiClient.post("/notifications/test-push");
      toast.success("Alert sound & notification triggered!");
    } catch {
      toast.error("Failed to send test alert");
    }
  };

  if (!supported) return null;

  return (
    <div className="flex items-center gap-2">
      {subscribed ? (
        <Button
          variant="outline"
          size="sm"
          onClick={handleTestAlert}
          className="text-xs h-8 text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
          title="Sound alert is enabled. Click to test chime & banner!"
        >
          <Volume2 className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
          Test 9 AM Alert
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleSubscribe}
          disabled={loading}
          className="text-xs h-8 text-primary border-primary/30 hover:bg-primary/10 shadow-sm"
        >
          <BellRing className="w-3.5 h-3.5 mr-1.5 text-primary animate-pulse" />
          {loading ? "Enabling…" : "Enable 9 AM Alerts"}
        </Button>
      )}
    </div>
  );
}
